import React, { HTMLAttributes } from 'react'

import { Button, Container, Heading, Text } from '@hhgtech/hhg-components'
import Link from 'next/link'
import { useIntl } from 'react-intl'

import ChevronIcon from '@/public/images/chevronRightBlue.svg'
import CheckIcon from '@/public/svg/check-circle.svg'
import { formatUserProfileUrl } from 'api/dataTransform'
import { ToolsBannerCardV2 } from 'components/molecules/toolsBannerCardV2'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { assetImagePath } from 'interfaces/constants'
import { BabyPoopPage } from 'interfaces/types'

import {
  StyledBabyPoopHeader,
  StyledBabyPoopHeaderContent,
  StyledBabyPoopHeaderContentText,
  StyledBabyPoopHeaderReview,
  StyledBabyPoopListItem,
  StyledBabyPoopSignatureColorBar,
  StyledBabyPoopHeaderContentImage,
} from './index.styled'
import { colorMap } from '../helpers'

type BabyPoopListItemProps = {
  link: string
  item: BabyPoopPage['result'][number]
  wrapperAttributes?: HTMLAttributes<HTMLAnchorElement> & {
    [key: string]: string
  }
}
const BabyPoopListItem = ({
  item,
  link,
  wrapperAttributes,
}: BabyPoopListItemProps) => {
  const { formatMessage: f } = useIntl()

  const { image, textInput: title, color } = item
  if (!image || !title) return null
  return (
    <a
      href={link}
      style={{
        textDecoration: 'none',
      }}
      {...wrapperAttributes}
    >
      <StyledBabyPoopListItem
        backgroundColor={colorMap[color]?.backgroundColor}
        borderColor={colorMap[color]?.borderColor}
      >
        <img src={image} alt={title} loading="lazy" />
        <StyledBabyPoopSignatureColorBar
          signatureColor={colorMap[color]?.displayColor || color}
          width={48}
          className="mt-4"
        />
        <Text size="s3" type="semiBold" className="mt-2">
          {title}
        </Text>
        <Text size="s5" type="semiBold" className="mt-2"></Text>
        <span className="item-see-more">
          {f({
            id: 'home.seeMore',
            defaultMessage: 'See More',
          })}
          <ChevronIcon className="pt-1" />
        </span>
      </StyledBabyPoopListItem>
    </a>
  )
}

type BabyPoopHeaderProps = {
  babyPoopPage: BabyPoopPage
  pageItem: { value: string; label: string }[]
  isVN?: boolean
  onClickFindOut?: () => void
}
const BabyPoopHeader = ({
  babyPoopPage,
  onClickFindOut,
  pageItem,
  isVN,
}: BabyPoopHeaderProps) => {
  const { formatMessage: f } = useIntl()
  const isMobile = useIsMobile()

  const currentUrl = useCurrentUrl()

  const { reviewedBy, reviewedDate, isSponsored, sponsorBanner } =
    babyPoopPage || {}

  const backgroundImage =
    (!isMobile ? sponsorBanner?.desktopImage : sponsorBanner?.mobileImage) ||
    undefined

  return isVN ? (
    <StyledBabyPoopHeader
      backgroundImage={backgroundImage}
      data-is-vietnam="true"
    >
      <Container
        className="px-4"
        style={{
          height: '100%',
        }}
      >
        <StyledBabyPoopHeaderContent data-is-vietnam="true">
          <StyledBabyPoopHeaderContentText isSponsored={isSponsored}>
            <Heading tag="h2" as="h1">
              {babyPoopPage.title}
            </Heading>
            <Text size="p2" className="description">
              {babyPoopPage.description}
            </Text>
            {!!reviewedBy && (
              <StyledBabyPoopHeaderReview className="reviewer">
                <CheckIcon />
                <Text size="p3">
                  {f({
                    id: 'categoryPage.info.reviewed',
                  })}
                  &nbsp;
                  <Link prefetch={false} href={reviewedBy.profileUrl}>
                    {reviewedBy.displayName}
                  </Link>
                  &nbsp;
                  {f(
                    { id: 'categoryPage.onDate' },
                    { date: reviewedDate || '' },
                  )}
                </Text>
              </StyledBabyPoopHeaderReview>
            )}
            <Button
              color="primary"
              size="md"
              className="mt-4 find-out-button"
              onClick={onClickFindOut}
              dataEventCategory="Health Tool"
              dataEventAction="Baby Stool Inquiry"
              dataEventLabel={currentUrl}
              style={{
                visibility: Boolean(onClickFindOut) ? 'visible' : 'hidden',
              }}
            >
              {f({
                id: 'babyPoop.findOut',
                defaultMessage: 'Find out now',
              })}
            </Button>
          </StyledBabyPoopHeaderContentText>
          <StyledBabyPoopHeaderContentImage
            src={babyPoopPage.featuredImage}
            alt={babyPoopPage.title}
            data-is-vietnam="true"
          />
        </StyledBabyPoopHeaderContent>
      </Container>
    </StyledBabyPoopHeader>
  ) : (
    <ToolsBannerCardV2
      breadcrumbProps={{ pageItem }}
      heading={babyPoopPage.title}
      description={babyPoopPage.description}
      icon={
        <img
          className="health-tool-icon"
          alt="health-tool-icon"
          loading="lazy"
          src={babyPoopPage?.featuredImage || `${assetImagePath}/baby-poop.svg`}
        />
      }
      reviewBy={{
        avatar: babyPoopPage.reviewedBy?.avatarThumbnail || '',
        reviewer: babyPoopPage.reviewedBy?.displayName || '',
        date: babyPoopPage.reviewedDate || '',
        link: formatUserProfileUrl(babyPoopPage?.reviewedBy as any),
      }}
      isMobile={isMobile}
    />
  )
}

export { BabyPoopListItem, BabyPoopHeader }
